/**
 * POPULATION.STORE.TS
 */
import { createEntityStore } from "@/core/factory/store"
import { createEmptyPopulation, Population, POPULATION_ENTITY } from "./population.types"
import { createSelector } from "@reduxjs/toolkit"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import { formatPopulation } from "./population.formatter"
import physicalRiskStore, { PhysicalRiskContext } from "@/entities/physical-risk/model/physical-risk.store"
import siteStore from "@/entities/site/model/site.store"
import { ESTABLISHMENT_ENTITY, Establishment } from "@/entities/establishment/model/establishment.types"

export type PopulationContext = {
  physicalRisks: PhysicalRisk[]
  physicalRiskContext: PhysicalRiskContext
}

const physicalRiskContextSelector = createSelector(
  [physicalRiskStore.selectors.list, physicalRiskStore.selectors.context],
  (physicalRisks, physicalRiskContext) => ({ physicalRisks, physicalRiskContext })
)

const createPopulationStore = () => {
  const base = createEntityStore<Population, PopulationContext>(
    POPULATION_ENTITY,
    createEmptyPopulation,
    physicalRiskContextSelector,
    formatPopulation
  )

  /**
   * List of populations filtered by site / risks
   */
  const listFiltered = createSelector(
    [
      base.selectors.list,
      physicalRiskStore.selectors.list,
      siteStore.selectors.list,
      (state) => state.site.selected,
      (state) => state.physicalRisk.selected
    ],
    (populations, risks, sites, selectedSites: string[], selectedRisks: string[]) => {
      const riskPopIds = selectedRisks.length
        ? risks.filter((x) => selectedRisks.includes(x.id)).flatMap((x) => x.populationIds ?? [])
        : null

      const sitePopIds = selectedSites.length
        ? sites.filter((x) => selectedSites.includes(x.id)).flatMap((x) => x.populationIds ?? [])
        : null

      return populations.filter(
        (x) => (!riskPopIds || riskPopIds.includes(x.id)) && (!sitePopIds || sitePopIds.includes(x.id))
      )
    }
  )

  /**
   * List for the active site
   */
  const listForSite = createSelector([base.selectors.list, (state) => state.site.draft], (populations, site) => {
    return populations.filter((x) => site.populationIds?.includes(x.id))
  })

  /**
   * Get all population for one risk
   */
  const listForRisk = createSelector(
    [base.selectors.list, (_: any, risk?: PhysicalRisk) => risk, physicalRiskStore.selectors.draft],
    (populations: Population[], risk?: PhysicalRisk, draftRisk?: PhysicalRisk) => {
      const r = risk ?? draftRisk
      if (!r) return []
      return populations.filter((x) => r.populationIds.includes(x.id))
    }
  )

  const listForActiveEstablishment = createSelector(
    [base.selectors.list, (state) => state[ESTABLISHMENT_ENTITY].active as Establishment],
    (populations, activeEstablishment) => {
      if (!activeEstablishment?.id) return populations
      return populations.filter((population) => population.EstablishmentId === activeEstablishment.id)
    }
  )

  return {
    ...base,
    selectors: {
      ...base.selectors,
      listForActiveEstablishment,
      listFiltered,
      listForSite,
      listForRisk
    }
  }
}

const populationStore = createPopulationStore()
export default populationStore
